import React, { useState, useEffect } from 'react';
import {
  Pane,
  Card,
  Table,
  Text,
  Button,
  majorScale,
  Spinner,
  Alert,
  Strong,
  Heading,
  Badge,
  IconButton,
  DownloadIcon,
} from 'evergreen-ui';
import { useCsvImport } from './hooks/useCsvImport';

const ImportSummary = ({ importSession, onError }) => {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingErrorCsv, setIsDownloadingErrorCsv] = useState(false);
  const { getImportSummary, downloadErrorReport, downloadErrorCsv } = useCsvImport();

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const data = await getImportSummary(importSession.id);
        setSummary(data);
      } catch (error) {
        onError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadSummary();
  }, [importSession.id]);

  const handleDownloadErrorReport = async () => {
    try {
      setIsDownloading(true);
      await downloadErrorReport(importSession.id);
    } catch (error) {
      onError(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadErrorCsv = async () => {
    try {
      setIsDownloadingErrorCsv(true);

      // Check if we have skipped rows from validation
      const skippedRowsKey = `skipped_rows_${importSession.id}`;
      const skippedRowsJson = localStorage.getItem(skippedRowsKey);

      // If we have skipped rows, pass them to the backend
      if (skippedRowsJson) {
        const skippedRows = JSON.parse(skippedRowsJson);
        // Include skipped rows in the request
        await downloadErrorCsv(importSession.id, { include_skipped_rows: true, skipped_rows: skippedRows });
        // Clean up localStorage
        localStorage.removeItem(skippedRowsKey);
      } else {
        // Regular download without skipped rows
        await downloadErrorCsv(importSession.id);
      }
    } catch (error) {
      onError(error.message);
    } finally {
      setIsDownloadingErrorCsv(false);
    }
  };

  if (isLoading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
        <Spinner />
      </Pane>
    );
  }

  if (!summary) {
    return (
      <Alert
        intent="danger"
        title="Error Loading Summary"
        marginBottom={majorScale(2)}
      >
        Unable to load import summary. Please try refreshing the page.
      </Alert>
    );
  }

  const getStatusBadge = (status) => {
    const colors = {
      completed: 'green',
      failed: 'red',
      cancelled: 'yellow',
    };

    return (
      <Badge color={colors[status] || 'neutral'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <Pane>
      <Card
        backgroundColor="white"
        elevation={0}
        border="default"
        padding={majorScale(2)}
        marginBottom={majorScale(2)}
      >
        <Pane marginBottom={majorScale(3)}>
          <Heading size={500} marginBottom={majorScale(2)}>
            Import Summary {getStatusBadge(summary.status)}
          </Heading>

          <Alert
            intent={summary.status === 'completed' ? 'success' : 'warning'}
            title="Import Results"
            marginBottom={majorScale(2)}
          >
            {summary.message}
          </Alert>
        </Pane>

        <Pane marginBottom={majorScale(3)}>
          <Heading size={400} marginBottom={majorScale(1)}>Statistics</Heading>
          <Table>
            <Table.Body>
              <Table.Row>
                <Table.TextCell>
                  <Strong>Total Rows Processed</Strong>
                </Table.TextCell>
                <Table.TextCell>{summary.total_rows}</Table.TextCell>
              </Table.Row>
              <Table.Row intent="success">
                <Table.TextCell>
                  <Strong>Successfully Imported</Strong>
                </Table.TextCell>
                <Table.TextCell>{summary.created_count + summary.updated_count}</Table.TextCell>
              </Table.Row>
              {summary.updated_count > 0 && (
                <Table.Row intent="warning">
                  <Table.TextCell>
                    <Strong>Updated Records</Strong>
                  </Table.TextCell>
                  <Table.TextCell>{summary.updated_count}</Table.TextCell>
                </Table.Row>
              )}
              {summary.error_count > 0 && (
                <Table.Row intent="danger">
                  <Table.TextCell>
                    <Strong>Unsuccessful</Strong>
                  </Table.TextCell>
                  <Table.TextCell>{summary.error_count} (see below for details)</Table.TextCell>
                </Table.Row>
              )}
              <Table.Row>
                <Table.TextCell>
                  <Strong>Processing Time</Strong>
                </Table.TextCell>
                <Table.TextCell>{summary.processing_time || 'N/A'} seconds</Table.TextCell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Pane>

        {/* Skipped Items Details Table (Added) */}
        {summary.skipped_details && summary.skipped_details.length > 0 && (
          <Pane marginBottom={majorScale(3)}>
            <Heading size={400} marginBottom={majorScale(1)} color="warning">Skipped Items</Heading>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>Row</Table.TextHeaderCell>
                <Table.TextHeaderCell>Reason</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {summary.skipped_details.map((skipped, index) => (
                  <Table.Row key={`skipped-${index}`} intent="warning">
                    {/* Add 1 to row_index because it's 0-based */}
                    <Table.TextCell>{skipped.row_index + 1}</Table.TextCell>
                    <Table.TextCell>{skipped.message}</Table.TextCell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <Text size={300} marginTop={majorScale(1)} color="muted">
              These rows were processed, but the specific item could not be added to an order (e.g., Product ISBN not found). The rest of the order may have imported successfully.
            </Text>
          </Pane>
        )}

        {summary.error_details && summary.error_details.length > 0 && (
          <Pane marginBottom={majorScale(3)}>
            <Heading size={400} marginBottom={majorScale(1)}>Error Details</Heading>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>Row</Table.TextHeaderCell>
                <Table.TextHeaderCell>Error</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {summary.error_details.map((error, index) => (
                  <Table.Row key={index} intent="danger">
                    <Table.TextCell>{error.row_index + 1}</Table.TextCell>
                    <Table.TextCell>{error.error_message}</Table.TextCell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            <Pane marginTop={majorScale(2)} display="flex" gap={majorScale(2)}>
              <Button
                iconBefore={DownloadIcon}
                onClick={handleDownloadErrorReport}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download Error Report'}
              </Button>
              <Button
                iconBefore={DownloadIcon}
                onClick={handleDownloadErrorCsv}
                disabled={isDownloadingErrorCsv}
                intent="success"
              >
                {isDownloadingErrorCsv ? 'Downloading...' : 'Download Unrecorded Data CSV'}
              </Button>
            </Pane>
          </Pane>
        )}

        <Pane marginTop={majorScale(3)}>
          <Text size={300} color="muted">
            Import ID: {importSession.id}
          </Text>
          <br />
          <Text size={300} color="muted">
            Completed at: {new Date(summary.completed_at).toLocaleString()}
          </Text>
        </Pane>
      </Card>

      <Pane display="flex" justifyContent="flex-end">
        <Button
          appearance="primary"
          onClick={() => window.location.reload()}
          marginRight={majorScale(2)}
        >
          Finish
        </Button>
      </Pane>
    </Pane>
  );
};

export default ImportSummary;
